import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';

import HighlightedText from '@zr/components/HighlightedText';

import { fetchItems } from './api';
import stateReducer from './stateReducer';

import '@static/css/light/vendor/zipAutocomplete.css';
import './Autocomplete.less';

export function Autocomplete({
  autocompleteUrl, className, error, inputProps, inputValue: parentInputValue,
  itemError, itemHint, itemToString, items, label, labelProps, onSuggestionsError,
  onInputValueChange, onSelectedItemChange, parse, prefix, showItemErrorClassByInput, touched,
}) {
  const [inputItems, setInputItems] = useState(items);
  // we use a state for this css class, because we only want to restyle after we check fetch results
  // this stops optimistically restyling item errors or not, which can appear like flickering between rerenders
  const [showItemErrorClass, setItemErrorClass] = useState(false);
  const {
    getComboboxProps, getInputProps, getItemProps, getLabelProps, getMenuProps, highlightedIndex, inputValue, isOpen,
  } = useCombobox({
    inputValue: parentInputValue,
    items: inputItems,
    itemToString,
    onInputValueChange: async ({ inputValue: newInputValue }) => {
      if (onInputValueChange) { onInputValueChange(newInputValue); }
      if (!newInputValue.trim()) { return; }
      try {
        const json = await fetchItems(autocompleteUrl, newInputValue);
        const parsedJson = parse(json);
        setInputItems(parsedJson);
        // parent component will calculate whether to show the error class or not by the input
        setItemErrorClass(showItemErrorClassByInput(newInputValue));
      } catch (e) {
        // if we ran into something unexpected, like a server error, notify the parent component
        onSuggestionsError(e);
      }
    },
    onSelectedItemChange,
    stateReducer,
  });

  const showError = touched && error;
  const inputLabel = showError ? error : label;
  // custom item error, will display if showing errors, they typed input, and have no suggestions
  // this represents they must select a suggestion, but their input yields no suggestion
  const showItemError = itemError && inputValue && !inputItems.length;

  return (
    <div className={`zipautocomplete ${inputValue ? 'filled' : ''} ${showError ? 'error' : ''} ${className}`}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
      <label {...getLabelProps(labelProps)}>{inputLabel}</label>
      <div {...getComboboxProps()}>
        <input {...getInputProps({
          ...inputProps,
        })}
        />
      </div>
      <ul className={`items ${prefix}-menu ${isOpen || showItemError ? `${prefix}-show` : ''}`} {...getMenuProps()}>
        {isOpen && !!inputItems.length && (
          <>
            {itemHint && <li className="item_hint">{itemHint}</li>}
            {inputItems.map((item, index) => (
              <li
                key={`${item}${index.toString()}`}
                className={`${prefix}-button ${highlightedIndex === index ? `${prefix}-selected` : ''}`}
                {...getItemProps({ item, index })}
              >
                <HighlightedText text={itemToString(item)} highlight={inputValue} highlightProps={{ className: `${prefix}-term` }} />
              </li>
            ))}
          </>
        )}
        {/* screen reader will announce no results are available if gotten here */}
        {showItemError && <li className={`item_hint ${showItemErrorClass ? 'error' : ''}`}>{itemError}</li>}
      </ul>
    </div>
  );
}

Autocomplete.propTypes = {
  autocompleteUrl: PropTypes.string.isRequired,
  className: PropTypes.string,
  error: PropTypes.string,
  inputProps: PropTypes.object,
  inputValue: PropTypes.string,
  itemError: PropTypes.string,
  itemHint: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.any), // eslint-disable-line react/forbid-prop-types
  itemToString: PropTypes.func,
  label: PropTypes.string.isRequired,
  labelProps: PropTypes.object,
  onInputValueChange: PropTypes.func,
  onSelectedItemChange: PropTypes.func,
  onSuggestionsError: PropTypes.func,
  parse: PropTypes.func,
  prefix: PropTypes.string,
  showItemErrorClassByInput: PropTypes.func,
  touched: PropTypes.bool,
};

Autocomplete.defaultProps = {
  className: '',
  error: null,
  inputProps: {},
  inputValue: undefined,
  itemError: null,
  itemHint: null,
  items: [],
  itemToString: () => {},
  labelProps: {},
  onInputValueChange: null,
  onSelectedItemChange: () => {},
  onSuggestionsError: () => {},
  parse: (json) => json,
  prefix: 'autocomplete',
  showItemErrorClassByInput: () => {},
  touched: false,
};

export default Autocomplete;
